<template>
  <slice
    :id="block._key"
    v-waypoint="{
      active: true,
      callback: onWaypoint,
      options: intersectionOptions,
    }"
    class="slice slice--teasing-video slice--full-height"
    :class="getClass()"
  >
    <div class="slice-video">
      <client-only>
        <div class="slice-video__container">
          <div
            class="slice-video__container__inner"
            :style="{
              width: `${videoSize.width}px`,
              height: `${videoSize.height}px`,
            }"
          >
            <video-bunny
              :src="`https://iframe.mediadelivery.net/embed/${videoFolder}/${videoId}?autoplay=true&loop=true&muted=true`"
            />
          </div>
        </div>
      </client-only>
    </div>
    <div class="container">
      <div class="row-md justify-center">
        <div class="col-md-1"></div>
        <div class="col-md-5 text">
          <portable-text
            v-if="block.text && block.text.length > 0"
            :blocks="block.text"
          />

          <big-count-down :date="countdownDate" class="mt-1-5 mt-md-2" />

          <div class="mt-1 mt-md-2">
            <form-klaviyo-list
              :id="`background-content-${block._key}`"
              :context="block.target"
              :success-text="block.success_text"
              :origin="$route.params.uid"
            />
          </div>
        </div>
        <div class="col-md-1"></div>
      </div>

      <div
        v-if="block.show_arrow"
        ref="icon"
        class="slice__arrow d-flex align-center justify-center pt-1-5 pt-md-3 pb-1-5 pb-md-2 mt-md-1 text-color-tertiary"
      >
        <icon name="arrow-down" />
      </div>
    </div>
  </slice>
</template>

<script>
import NuxtSSRScreenSize from 'nuxt-ssr-screen-size'

import Slice from '@/components/Slices/Slice'
import PortableText from '@/components/RichText/PortableText'
import VideoBunny from '@/components/video/bunny'
import Icon from '@/components/icons/Icon'

import { BUNNY_STREAM_FOLDERS } from '@/const'

export default {
  name: 'BackgroundContent',
  components: {
    Slice,
    PortableText,
    VideoBunny,
    Icon,
    FormKlaviyoList: () =>
      import(
        '@/components/form/klaviyo/List' /* webpackChunkName: "form-klaviyo-list" */
      ),

    BigCountDown: () =>
      import(
        '@/components/countdown/BigCountDown' /* webpackChunkName: 'big_countdown' */
      ),
  },
  mixins: [NuxtSSRScreenSize.NuxtSSRScreenSizeMixin],
  props: {
    block: { type: Object, required: true },
  },
  data() {
    return {
      imageInScreen: false,
      intersectionOptions: {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: [0.2, 1], // [0.25, 0.75] if you want a 25% offset!
      },
    }
  },
  computed: {
    videoFolder() {
      return this.$vssWidth < 769
        ? BUNNY_STREAM_FOLDERS[this.block.video_mobile.folder]
        : BUNNY_STREAM_FOLDERS[this.block.video.folder]
    },
    videoId() {
      return this.$vssWidth < 769
        ? this.block.video_mobile.id
        : this.block.video.id
    },
    videoSize() {
      let ratio
      if (this.$vssWidth < 769) {
        ratio = 9 / 16
      } else {
        ratio = 16 / 9
      }

      const screenRatio = this.$vssWidth / this.$vssHeight

      if (screenRatio <= ratio) {
        return {
          width: Math.round(this.$vssHeight * ratio),
          height: this.$vssHeight,
        }
      }

      return {
        width: this.$vssWidth,
        height: Math.round(this.$vssWidth / ratio),
      }
    },
    countdownDate() {
      return new Date(this.block.date)
    },
  },
  methods: {
    getClass() {
      const cc = []

      if (this.imageInScreen) {
        cc.push('slice-in-screen')
      }

      if (this.block.show_arrow) {
        cc.push('slice--with-arrow')
      }

      return cc
    },

    onWaypoint({ going }) {
      // going: in, out
      // direction: top, right, bottom, left
      if (going === this.$waypointMap.GOING_IN) {
        this.imageInScreen = true
      }

      if (going === this.$waypointMap.GOING_OUT) {
        this.imageInScreen = false
      }
    },
  },
}
</script>

<style lang="scss">
.slice--teasing-video {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-left: auto;
  margin-right: auto;
  transition: max-width 0.6s;
  min-height: 100svh;
  padding: rem($spacing * 4) 0 0;

  .slice-video {
    &__container {
      position: absolute;
      inset: 0;
      pointer-events: none;
      overflow: hidden;

      &__inner {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }
  }

  @include mq($from: tablet) {
    position: relative;
    padding: rem($spacing * 8) 0 0;
    overflow: hidden;
  }

  @include mq($until: tablet) {
    .container {
      transition: padding 0.6s;
    }
  }

  .text {
    padding: calc(var(--spacing) * 2);
    background: RGB(255 255 255 / 0.9);
    text-align: center;
    z-index: 1;

    h3 {
      margin-bottom: calc(var(--spacing) * 1.5);
    }

    @include mq($from: tablet) {
      padding: calc(var(--spacing) * 4);
    }
  }

  .big-countdown {
    justify-content: center;
  }

  .big-countdown .number,
  .big-countdown .interval {
    color: var(--secondary-color);
  }

  .slice__arrow {
    position: relative;
    z-index: 1;

    .icon {
      width: 28px;
      height: 28px;
    }
  }

  form {
    .action {
      width: 100%;
    }
  }
}
</style>
